<template>
	<div class="to-timeline" :flex="flex ? flex : null" :class="getClass">
		<div v-for="(item, index) in displayItems" :key="index" class="to-timeline-item" :style="getItemStyle()" @click="handleItemClick(item, index)">
			<!-- 外部时间显示 -->
			<div v-if="showTimeOutside(item)" class="to-timeline-datetime">
				<div v-if="item.date" class="to-timeline-date">
					<div class="to-timeline-date-text">{{ item.date }}</div>
				</div>
				<div v-if="item.time" class="to-timeline-time">
					<div class="to-timeline-time-text">{{ item.time }}</div>
				</div>
			</div>

			<!-- 图标 -->
			<to-icon class="to-timeline-icon" :value="item.icon" :color="item.color" :mode="item.mode">
				{{ item.text }}
			</to-icon>

			<!-- 主内容区域 -->
			<div class="to-timeline-main">
				<!-- 内部时间显示 -->
				<div v-if="showTimeInside(item)" class="to-timeline-datetime">
					<div v-if="item.date" class="to-timeline-date">
						<div class="to-timeline-date-text">{{ item.date }}</div>
					</div>
					<div v-if="item.time" class="to-timeline-time">
						<div class="to-timeline-time-text">{{ item.time }}</div>
					</div>
				</div>

				<!-- 内容区域 -->
				<div class="to-timeline-content" :style="item.style">
					<template v-if="item.content">{{ item.content }}</template>
					<slot name="default" :item="item" :index="index"></slot>
				</div>
			</div>

			<div v-if="index < displayItems.length - 1 || tail" class="to-timeline-line"></div>
		</div>
	</div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, watch, onMounted, nextTick } from 'vue'
import { ToIcon } from '../../icon'
import type { TimelineItem, TimelineTimePosition, TimelineDirection, TimelineAlign, TimelineMode } from './timeline'

export default defineComponent({
	name: 'ToTimeline',
	components: {
		ToIcon
	},
	props: {
		flex: Boolean,
		data: {
			type: Array as PropType<TimelineItem[]>,
			default: () => []
		},
		timePosition: {
			type: String as PropType<TimelineTimePosition>,
			default: 'in',
			validator: (val: string) => ['in', 'out'].includes(val)
		},
		dir: {
			type: String as PropType<TimelineDirection>,
			default: 'v',
			validator: (val: string) => ['v', 'h', ''].includes(val)
		},
		mode: {
			type: String as PropType<TimelineMode>,
			default: ''
		},
		align: {
			type: String as PropType<TimelineAlign>,
			default: 'left',
			validator: (val: string) => ['left', 'center', 'right'].includes(val)
		},
		itemWidth: {
			type: [String, Number],
			default: ''
		},
		reverse: {
			type: Boolean,
			default: false
		},
		tail: Boolean
	},
	emits: ['click', 'item-rendered'],
	setup(props, { emit }) {
		// 处理数据顺序
		const displayItems = computed(() => {
			if (props.reverse) {
				return [...props.data].reverse()
			}
			return props.data
		})

		// 组件基础类名
		const getClass = computed(() => {
			const classArray = []

			// 布局类型
			if (props.dir === 'h') {
				classArray.push('dir-h')
			}

			// 时间位置
			if (props.timePosition === 'out') {
				classArray.push('time-position-out')
			}

			// 对齐方式
			if (props.align === 'center') {
				classArray.push('align-center')
			} else if (props.align === 'right') {
				classArray.push('align-right')
			}

			// 全局模式类
			if (props.mode) {
				classArray.push(`mode-${props.mode}`)
			}

			// 是否含有 date
			if (props.data.some(item => item.date)) {
				classArray.push(`has-date`)
			}

			// 是否含有 time
			if (props.data.some(item => item.time)) {
				classArray.push(`has-time`)
			}

			// 是否设置 item-width
			if (props.itemWidth) {
				classArray.push(`is-item-width`)
			}

			return classArray
		})

		// 获取图标模式
		const getIconMode = (item: TimelineItem) => {
			// 优先使用全局模式，如果未设置则使用项目自身模式
			if (props.mode) {
				return props.mode
			}
			return item.mode
		}

		// 获取内容样式
		const getItemStyle = () => {
			const style: Record<string, string> = {}

			// 处理宽度设置 - 仅在水平布局时有效
			if (props.dir === 'h' && props.itemWidth) {
				if (!isNaN(Number(props.itemWidth))) {
					style.width = props.itemWidth + 'em'
				} else {
					style.width = props.itemWidth.toString()
				}
			}

			return style
		}

		// 判断是否显示外部时间
		const showTimeOutside = (item: TimelineItem) => {
			return props.timePosition === 'out' && (item.date || item.time)
		}

		// 判断是否显示内部时间
		const showTimeInside = (item: TimelineItem) => {
			return props.timePosition === 'in' && (item.date || item.time)
		}

		// 点击事件处理
		const handleItemClick = (item: TimelineItem, index: number) => {
			emit('click', item, index)
		}

		// 监听数据变化
		watch(
			() => props.data,
			() => {
				nextTick(() => {
					props.data.forEach((_item, _index) => {
						emit('item-rendered', _item, _index)
					})
				})
			},
			{ immediate: false, deep: true }
		)

		// 初始化时触发渲染完成事件
		onMounted(() => {
			nextTick(() => {
				props.data.forEach((_item, _index) => {
					emit('item-rendered', _item, _index)
				})
			})
		})

		return {
			displayItems,
			getClass,
			getIconMode,
			getItemStyle,
			showTimeOutside,
			showTimeInside,
			handleItemClick
		}
	}
})
</script>
